import { AsyncOperationError, ServiceError } from '../errors'
import { userAuthHeader } from '../apiClient'
import { newRequestId, fetchOrThrowNetworkError } from './utils'
import type {
  TOperationStatus,
  IAsyncOperationResponse,
  IInFlightOperation,
} from '~/types/asyncOperation/models'
import { HTTPStatus } from '~/api/httpStatus'

const STORAGE_KEY = 'asyncOps'
const INITIAL_POLL_INTERVAL_MS = 500
const BACKOFF_THRESHOLD_MS = 5000
const BACKOFF_POLL_INTERVAL_MS = 1500
const MAX_POLL_INTERVAL_MS = 5000
const MAX_WAIT_MS = 120000

export async function pollOperation(
  operationId: string,
  callingMn: string,
  onPending?: (status: TOperationStatus) => void
): Promise<any> {
  const fn = `${callingMn} --> pollOperation |`
  const apiPath = `/api/operations/${operationId}`
  let elapsedMs = 0
  let intervalMs = INITIAL_POLL_INTERVAL_MS

  console.debug(`${fn} Starting poll for operation ${operationId}`)

  while (elapsedMs < MAX_WAIT_MS) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
    elapsedMs += intervalMs

    if (
      elapsedMs > BACKOFF_THRESHOLD_MS &&
      intervalMs < BACKOFF_POLL_INTERVAL_MS
    ) {
      intervalMs = BACKOFF_POLL_INTERVAL_MS
    }
    if (intervalMs > MAX_POLL_INTERVAL_MS) {
      intervalMs = MAX_POLL_INTERVAL_MS
    }

    const headers = Object.assign(
      { 'x-request-id': newRequestId() },
      userAuthHeader()
    )
    console.debug(`${fn} Request Id: ${headers['x-request-id']}`)

    const response = await fetchOrThrowNetworkError(apiPath, {
      method: 'GET',
      headers,
    })

    if (response.status === HTTPStatus.Accepted) {
      const body: IAsyncOperationResponse = await response.json()
      console.debug(
        `${fn} Operation ${operationId} status: ${body.data.status} (${elapsedMs}ms elapsed)`
      )
      if (onPending) {
        onPending(body.data.status)
      }
      continue
    }

    if (response.status === HTTPStatus.OK) {
      const body: IAsyncOperationResponse = await response.json()
      const op = body.data

      if (op.status === 'succeeded') {
        console.debug(
          `${fn} Operation ${operationId} succeeded after ${elapsedMs}ms`
        )
        const result = op.result as { data: any }
        return result.data
      }

      if (op.status === 'failed') {
        const errorResult = op.result as {
          error: { type: string; message: string; details?: any }
        }
        console.error(
          `${fn} Operation ${operationId} failed: ${errorResult.error.type} — ${errorResult.error.message}`
        )
        throw new AsyncOperationError(
          errorResult.error.message,
          errorResult.error.type,
          operationId,
          errorResult.error.details
        )
      }
    }

    if (response.status === HTTPStatus.NotFound) {
      throw new ServiceError(
        404,
        `Operation ${operationId} not found (expired or different auth context)`
      )
    }

    // OUR timeout for a long-running operation (Heroku's is 503 — see below).
    // The work is not lost: the server gave up WAITING, not processing. So this
    // is the same event as the MAX_WAIT_MS expiry at the bottom of this function
    // seen from the other side, and it must produce the same outcome —
    // handleAsyncDataErrors renders errorType 'Timeout' as a non-destructive
    // "still processing, it will complete in the background" notification.
    //
    // Without this branch a 504 fell through to the catch-all below and became a
    // bare ServiceError, which isIServiceError() routes to context.error() — so
    // an EXPECTED timeout replaced the whole SPA with an error page reading
    // "Unexpected status 504 polling operation <id>": destructive, and showing
    // an internal string and an opaque id to a user.
    //
    // 503 deliberately does NOT get the same treatment. Heroku returns it for
    // several unrelated conditions (H12 timeout, H11 backlog, H13/H18/H19
    // connection errors, R14 memory, dyno down) and only the Heroku router log
    // distinguishes them, so a 503 cannot be mapped to a cause and any message
    // naming one would be wrong whenever it was not a timeout.
    if (response.status === HTTPStatus.GatewayTimeout) {
      throw new AsyncOperationError(
        `Operation ${operationId} timed out server-side (504)`,
        'Timeout',
        operationId
      )
    }

    throw new ServiceError(
      response.status,
      `Unexpected status ${response.status} polling operation ${operationId}`
    )
  }

  throw new AsyncOperationError(
    `Operation ${operationId} still processing after ${MAX_WAIT_MS / 1000}s`,
    'Timeout',
    operationId
  )
}

export function saveInFlightOperation(
  operationId: string,
  apiPath: string
): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  const ops = getInFlightOperations()
  ops.push({ operationId, apiPath, startedAt: new Date().toISOString() })
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ops))
}

export function getInFlightOperations(): IInFlightOperation[] {
  if (typeof sessionStorage === 'undefined') {
    return []
  }
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) {
    return []
  }
  try {
    return JSON.parse(raw)
  } catch {
    return []
  }
}

export function removeInFlightOperation(operationId: string): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  const ops = getInFlightOperations().filter(
    (op) => op.operationId !== operationId
  )
  if (ops.length === 0) {
    sessionStorage.removeItem(STORAGE_KEY)
  } else {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ops))
  }
}
